import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { configApi } from '../../services/api';

const ICON_PATHS = {
  truck:
    'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.05-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12 0V9.677m0 0a3 3 0 01-5.196 0m5.196 0A1.5 1.5 0 0118 9.676V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0z',
  package:
    'M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
  zap: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  clock:
    'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  gift:
    'M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
};

const BarIcon = ({ type, color }) => (
  <svg
    className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]"
    fill="none"
    viewBox="0 0 24 24"
    stroke={color || 'currentColor'}
    strokeWidth={1.75}
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[type] || ICON_PATHS.truck} />
  </svg>
);

export const buildShippingBarViewModel = (config, t) => {
  const fallback = {
    enabled: true,
    titleText: t('shippingAnnouncement.label'),
    messageText: t('shippingAnnouncement.message'),
    messageShortText: t('shippingAnnouncement.messageShort'),
    ariaLabel: t('shippingAnnouncement.ariaLabel'),
    titleColor: '#fef3c7',
    titleBgColor: 'rgba(251,191,36,0.15)',
    titleRingColor: 'rgba(251,191,36,0.35)',
    messageColor: '#f4f4f5',
    barBgColor: '#09090b',
    barBorderColor: 'rgba(63,63,70,0.95)',
    innerBgColor: 'rgba(24,24,27,0.9)',
    innerRingColor: 'rgba(82,82,91,0.75)',
    accentColor: '#fbbf24',
    shimmerColor: 'rgba(251,191,36,0.4)',
    iconColor: '#fcd34d',
    iconBgColor: 'rgba(24,24,27,0.95)',
    iconRingColor: 'rgba(82,82,91,0.8)',
    useBarGradient: false,
    barGradientFrom: '#09090b',
    barGradientTo: '#18181b',
    barGradientAngle: 90,
    iconType: 'truck',
    animationsEnabled: true,
    tickerDurationSeconds: 16,
  };

  if (!config) return fallback;
  return { ...fallback, ...config };
};

export const ShippingAnnouncementBarView = ({ view }) => {
  if (!view?.enabled) return null;

  const barBackground = view.useBarGradient
    ? `linear-gradient(${view.barGradientAngle}deg, ${view.barGradientFrom}, ${view.barGradientTo})`
    : view.barBgColor;

  const motionClass = view.animationsEnabled ? '' : 'motion-reduce:animate-none';
  const tickerStyle = view.animationsEnabled
    ? { animationDuration: `${view.tickerDurationSeconds}s` }
    : undefined;

  const tickerItem = (
    <div className="mx-4 inline-flex shrink-0 items-center gap-2 sm:mx-6 sm:gap-3">
      <span
        className={`${motionClass} ${view.animationsEnabled ? 'animate-shipping-truck-bob' : ''} flex h-6 w-6 shrink-0 items-center justify-center rounded-md sm:h-7 sm:w-7`}
        style={{
          backgroundColor: view.iconBgColor,
          boxShadow: `inset 0 0 0 1px ${view.iconRingColor}`,
        }}
        aria-hidden
      >
        <BarIcon type={view.iconType} color={view.iconColor} />
      </span>
      <span
        className="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold uppercase tracking-[0.1em] sm:px-2.5 sm:text-[13px] sm:tracking-[0.12em]"
        style={{
          color: view.titleColor,
          backgroundColor: view.titleBgColor,
          boxShadow: `inset 0 0 0 1px ${view.titleRingColor}`,
        }}
      >
        {view.titleText}
      </span>
      <p
        className="min-w-0 max-w-[78vw] font-sans text-sm font-medium leading-snug sm:max-w-none sm:text-[0.9375rem] sm:leading-normal"
        style={{ color: view.messageColor }}
      >
        <span className="block truncate sm:hidden">{view.messageShortText}</span>
        <span className="hidden sm:block">{view.messageText}</span>
      </p>
    </div>
  );

  return (
    <div
      role="region"
      aria-label={view.ariaLabel}
      className={`site-shipping-announcement ${motionClass} ${view.animationsEnabled ? 'animate-shipping-bar-enter' : ''} fixed left-0 right-0 top-0 z-[1001] flex h-[var(--shipping-announcement-height)] min-h-[var(--shipping-announcement-height)] items-center overflow-hidden antialiased will-change-transform`}
      style={{
        background: barBackground,
        borderBottom: `1px solid ${view.barBorderColor}`,
        color: view.messageColor,
      }}
    >
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-[3px]"
        style={{
          background: `linear-gradient(to bottom, ${view.accentColor}33, ${view.accentColor}, ${view.accentColor}33)`,
        }}
        aria-hidden
      />

      <div className="relative z-[2] w-full px-0">
        <div
          className={`${motionClass} ${view.animationsEnabled ? 'animate-shipping-message-pop' : ''} relative overflow-hidden rounded-none py-1 sm:py-1.5`}
          style={{
            backgroundColor: view.innerBgColor,
            boxShadow: `inset 0 0 0 1px ${view.innerRingColor}`,
          }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
            <div
              className={`absolute inset-y-0 w-[30%] opacity-85 ${motionClass} ${view.animationsEnabled ? 'animate-shipping-box-shimmer' : ''}`}
              style={{
                background: `linear-gradient(to right, transparent, ${view.shimmerColor}, transparent)`,
              }}
            />
          </div>
          <div
            className={`relative z-10 flex w-max whitespace-nowrap ${motionClass} ${view.animationsEnabled ? 'animate-shipping-ticker-loop' : ''}`}
            style={tickerStyle}
          >
            {tickerItem}
            {tickerItem}
            {tickerItem}
          </div>
        </div>
      </div>
    </div>
  );
};

const ShippingAnnouncementBar = () => {
  const { t } = useTranslation();
  const [remoteConfig, setRemoteConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await configApi.getShippingAnnouncementConfig();
        if (!cancelled) setRemoteConfig(data);
      } catch {
        if (!cancelled) setRemoteConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(() => buildShippingBarViewModel(remoteConfig, t), [remoteConfig, t]);

  return <ShippingAnnouncementBarView view={view} />;
};

export default ShippingAnnouncementBar;
